// ============================================================================
// CORE ALGORITHM TYPES
// Type-safe interfaces for algorithm execution, visualization, and step data
// ============================================================================

// ============================================================================
// CORE TYPES
// ============================================================================

export interface ExecutionRequest {
  algorithmId: string;
  input: AlgorithmInput;
  options: ExecutionOptions;
}

export interface ExecutionResponse {
  executionId: string;
  steps: ExecutionStep[];
  result: ExecutionResult;
  metadata: ExecutionMetadata;
}

export interface ExecutionOptions {
  maxSteps?: number;
  timeout?: number;
  enableTracing?: boolean;
  returnHistory?: boolean;
  enableProfiling?: boolean;
}

export interface ExecutionMetadata {
  startTime: number;
  endTime: number;
  duration: number;
  memoryUsed: number;
  stepsCount: number;
  algorithmVersion: string;
  engineVersion: string;
}

// ============================================================================
// EXECUTION STEPS
// ============================================================================

export interface ExecutionStep {
  stepId: number;
  stepType: StepType;
  timestamp: number;
  payload: StepPayload;
  metadata: StepMetadata;
}

export enum StepType {
  Initialization = "initialization",
  StateChange = "state_change",
  Comparison = "comparison",
  Swap = "swap",
  Update = "update",
  Decision = "decision",
  Completion = "completion",
  Error = "error",
}

export interface StepMetadata {
  line?: number;
  complexity?: string;
  explanation?: string;
  highlightIds?: string[];
}

export type StepPayload =
  | GraphStatePayload
  | ArrayStatePayload
  | MatrixStatePayload
  | TreeStatePayload
  | StringStatePayload
  | GenericStatePayload;

// ============================================================================
// STATE PAYLOADS
// ============================================================================

export interface GraphStatePayload {
  type: "graph";
  vertices: Vertex[];
  edges: Edge[];
  metadata: GraphMetadata;
}

export interface ArrayStatePayload {
  type: "array";
  items: ArrayItem[];
  metadata: ArrayMetadata;
}

export interface MatrixStatePayload {
  type: "matrix";
  rows: number;
  cols: number;
  data: (number | string)[][];
  metadata: MatrixMetadata;
}

export interface TreeStatePayload {
  type: "tree";
  nodes: TreeNode[];
  edges: TreeEdge[];
  metadata: TreeMetadata;
}

export interface StringStatePayload {
  type: "string";
  value: string;
  metadata: StringMetadata;
}

export interface GenericStatePayload {
  type: "generic";
  data: Record<string, unknown>;
  metadata: Record<string, unknown>;
}

// ============================================================================
// GRAPH TYPES
// ============================================================================

export interface Vertex {
  id: string;
  value?: string | number;
  label?: string;
  x?: number;
  y?: number;
  color?: string;
  position?: { x: number; y: number };
}

export interface Edge {
  id?: string;
  from: string;
  to: string;
  weight?: number;
  label?: string;
  directed?: boolean;
  color?: string;
  // Flow algorithm fields
  flow?: number;
  capacity?: number;
  residual?: boolean;
}

export interface GraphMetadata {
  directed?: boolean;
  weighted?: boolean;
  startVertex?: string;
  currentVertex?: string;
  endVertex?: string;
  visitedVertices?: string[];
  currentEdge?: { id?: string; from: string; to: string };
  distances?: Record<string, number>;
  priorityQueue?: string[];
  queue?: string[];
  stack?: string[];
  path?: string[];
  components?: string[][];
  result?: unknown;
  // KD-tree spatial overlay
  kdAxis?: 'x' | 'y';
  kdSplitValue?: number;
  kdQueryRect?: { x: number; y: number; w: number; h: number };
  // Traversal tree overlay: edges of the BFS/DFS/Dijkstra spanning tree
  traversalTreeEdges?: { from: string; to: string }[];
  // Traversal order: maps vertex id → discovery order (1-indexed)
  traversalOrder?: Record<string, number>;
  // MST overlay: edges included in the minimum spanning tree (Prim/Kruskal/Boruvka)
  mstEdges?: { from: string; to: string }[];
}

// ============================================================================
// ARRAY TYPES
// ============================================================================

export interface ArrayItem {
  index: number;
  value: number | string;
  highlighted?: boolean;
  compared?: boolean;
  swapped?: boolean;
  sorted?: boolean;
}

export interface ArrayMetadata {
  currentIndex?: number;
  compareIndex?: number;
  compareIndices?: number[];
  highlightIndices?: number[];
  pivotIndex?: number;
  sortedIndices?: number[];
  heapifiedIndices?: number[];
  swappedIndices?: number[];
  swapped?: boolean;
  result?: unknown;
  // Fractional Knapsack / Job Sequencing extra fields
  itemLabels?: string[];
  itemWeights?: number[];
  itemValues?: number[];
  remainingCap?: number;
  totalValue?: number;
  mergeRange?: [number, number];
}

// ============================================================================
// MATRIX TYPES
// ============================================================================

export interface MatrixMetadata {
  currentCell?: { row: number; col: number };
  highlightedCells?: { row: number; col: number }[];
  rowHeaders?: string[];
  colHeaders?: string[];
  result?: unknown;
  splitPoint?: number;
  updated?: boolean;
}

// ============================================================================
// TREE TYPES
// ============================================================================

export interface TreeNode {
  id: string;
  value: string | number;
  label?: string;
  left?: string;
  right?: string;
  children?: string[];
  parent?: string;
  height?: number;
  color?: "red" | "black";
}

export interface TreeEdge {
  from: string;
  to: string;
  type?: "left" | "right" | "parent";
}

export interface TreeMetadata {
  currentNode?: string;
  visitedNodes?: string[];
  balanceFactors?: Record<string, number>;
  result?: unknown;
}

// ============================================================================
// STRING TYPES
// ============================================================================

export interface StringMetadata {
  currentIndex?: number;
  compareIndex?: number;
  matchedIndices?: number[];
  mismatchIndices?: number[];
  windowStart?: number;
  windowEnd?: number;
  failureTable?: number[];
  lpsTable?: number[];
  windowHash?: number | string;
  patternHash?: number | string;
  currentHash?: number | string;
  targetHash?: number | string;
  result?: unknown;
  word1?: string;
  word2?: string;
  pattern?: string;
  highlightIndices?: number[];
  zArray?: number[];
  suffixArray?: number[];
  automatonStates?: string[];
  prefixArray?: number[];
  linkArray?: number[];
}

// ============================================================================
// ALGORITHM INPUT
// ============================================================================

export interface AlgorithmInput {
  algorithmId: string;
  data: InputData;
}

export type InputData =
  | GraphInput
  | ArrayInput
  | MatrixInput
  | StringInput
  | TreeInput
  | GridInput
  | FlowInput
  | AutomatonInput
  | NumberTheoryInput
  | GenericInput;

export interface GraphInput {
  type: "graph";
  vertices: Vertex[];
  edges: Edge[];
  startNode?: string;
  endNode?: string;
}

export interface ArrayInput {
  type: "array";
  data: number[] | string[];
}

export interface MatrixInput {
  type: "matrix";
  data: number[][];
}

export interface StringInput {
  type: "string";
  word1: string;
  word2?: string;
}

export interface TreeInput {
  type: "tree";
  nodes: TreeNode[];
  edges: TreeEdge[];
}

export interface GridInput {
  type: "grid";
  rows: number;
  cols: number;
  cells: number[][];
  start?: { row: number; col: number };
  end?: { row: number; col: number };
  obstacles?: { row: number; col: number }[];
}

export interface FlowInput {
  type: "flow";
  vertices: Vertex[];
  edges: Edge[];
  source: string;
  sink: string;
}

export interface AutomatonInput {
  type: "automaton";
  states: string[];
  alphabet: string[];
  transitions: { from: string; to: string; symbol: string }[];
  startState: string;
  acceptStates: string[];
}

export interface NumberTheoryInput {
  type: "number-theory";
  n: number;
  operands?: number[];
}

export interface GenericInput {
  type: "generic";
  data: Record<string, unknown>;
}

// ============================================================================
// EXECUTION RESULT
// ============================================================================

export interface ExecutionResult {
  success: boolean;
  output: unknown;
  error?: ExecutionError;
  profiling?: ProfilingData;
}

export interface ExecutionError {
  code: string;
  message: string;
  stack?: string;
}

export interface ProfilingData {
  memoryPeak: number;
  cpuTime: number;
  operationsCount: number;
  comparisonsCount: number;
  swapsCount: number;
}

// ============================================================================
// ALGORITHM METADATA
// ============================================================================

export interface AlgorithmMetadata {
  id: string;
  name: string;
  category: AlgorithmCategory;
  tags: string[];
  complexity: ComplexityInfo;
  visualizationType: VisualizationType;
  supportedLanguages: Language[];
  eventTypes: EventType[];
  description: string;
}

export enum AlgorithmCategory {
  Graph = "graph",
  String = "string",
  DP = "dp",
  DataStructure = "data-structure",
  Math = "math",
  Game = "game",
  Logic = "logic",
  Geometry = "geometry",
  Sorting = "sorting",
  Backtracking = "backtracking",
  Search = "search",
  Greedy = "greedy",
}

export interface ComplexityInfo {
  time: string;
  space: string;
  best?: string;
  average?: string;
  worst?: string;
  explanation: string;
}

export enum VisualizationType {
  Graph = "graph",
  Array = "array",
  Matrix = "matrix",
  Tree = "tree",
  String = "string",
  Geometry = "geometry",
  RecursionTree = "recursion-tree",
  Automaton = "automaton",
  Generic = "generic",
  Grid = "grid",
  Flow = "flow",
}

export enum Language {
  Cpp = "cpp",
  CSharp = "csharp",
  Python = "python",
  Java = "java",
  JavaScript = "javascript",
  TypeScript = "typescript",
  Go = "go",
  Rust = "rust",
}

export enum EventType {
  NodeVisited = "node_visited",
  EdgeRelaxed = "edge_relaxed",
  Comparison = "comparison",
  Swap = "swap",
  Update = "update",
  Decision = "decision",
  Error = "error",
  Complete = "complete",
  DSUMerge = "dsu_merge",
  DSUFind = "dsu_find",
  SegmentTreeUpdate = "segment_tree_update",
  SegmentTreeQuery = "segment_tree_query",
  Flow = "flow",
  Augment = "augment",
  Cut = "cut",
  GridUpdate = "grid_update",
  Backtrack = "backtrack",
}

// ============================================================================
// EVENT SYSTEM
// ============================================================================

export interface AlgorithmEvent {
  eventId: number;
  eventType: EventType;
  timestamp: number;
  data: EventData;
}

export type EventData =
  | NodeVisitedData
  | EdgeRelaxedData
  | ComparisonData
  | SwapData
  | UpdateData
  | DecisionData
  | ErrorData
  | CompleteData
  | DSUMergeData
  | DSUFindData;

export interface NodeVisitedData {
  nodeId: string;
  value?: unknown;
}

export interface EdgeRelaxedData {
  from: string;
  to: string;
  weight: number;
  newDistance: number;
}

export interface ComparisonData {
  left: string | number;
  right: string | number;
  result: boolean;
}

export interface SwapData {
  index1: number;
  index2: number;
  value1: unknown;
  value2: unknown;
}

export interface UpdateData {
  target: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface DecisionData {
  condition: string;
  result: boolean;
  branch: "true" | "false";
}

export interface ErrorData {
  message: string;
  code: string;
}

export interface CompleteData {
  result: unknown;
  success: boolean;
}

export interface DSUMergeData {
  set1: number;
  set2: number;
  newSet: number;
}

export interface DSUFindData {
  element: number;
  set: number;
}

// ============================================================================
// REGISTRY
// ============================================================================

export interface AlgorithmRegistry {
  algorithms: Map<string, AlgorithmMetadata>;
  implementations: Map<string, ImplementationInfo[]>;
}

export interface ImplementationInfo {
  language: Language;
  filePath: string;
  version: string;
  lastUpdated: string;
  hasTests: boolean;
  hasExample: boolean;
}

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

export interface StateSnapshot {
  version: number;
  timestamp: number;
  data: StateData;
  checksum: string;
}

export type StateData =
  | GraphStatePayload
  | ArrayStatePayload
  | MatrixStatePayload
  | TreeStatePayload
  | StringStatePayload;

export interface StateManager {
  currentState: StateData;
  history: StateSnapshot[];
  maxHistory: number;
}

// ============================================================================
// VALIDATION
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  errorCode: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
}
